// Copyright (c) 2025 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import {
  DefaultApi,
  DisclosedContract,
  EventFormat,
  Filters,
  JsContractEntry,
  JsContractEntryOneOf,
} from '@digitalasset/json-api-ts-client';
import hash from 'object-hash';
import { v4 as uuidv4 } from 'uuid';
import { Amulet, LockedAmulet } from '@daml.js/splice-amulet/lib/Splice/Amulet';
import {
  AmuletRules,
  AmuletRules_DevNet_TapResult,
  AppTransferContext,
  TransferContext,
  TransferOutput,
  TransferPreapproval,
} from '@daml.js/splice-amulet/lib/Splice/AmuletRules';
import { OpenMiningRound } from '@daml.js/splice-amulet/lib/Splice/Round';
import { AllocationRequest } from '@daml.js/splice-api-token-allocation-request/lib/Splice/Api/Token/AllocationRequestV1';
import { Allocation } from '@daml.js/splice-api-token-allocation/lib/Splice/Api/Token/AllocationV1';
import { Holding as TokenStandardHolding } from '@daml.js/splice-api-token-holding/lib/Splice/Api/Token/HoldingV1';
import { TransferInstruction } from '@daml.js/splice-api-token-transfer-instruction/lib/Splice/Api/Token/TransferInstructionV1';
import {
  CollateralAgreement,
  CollateralAgreementChangeRequest,
  CollateralAgreementRequest,
  ExecutedCollateralTransfer,
  InstructedCollateral,
} from '@daml.js/utility-collateral-app-v1/lib/Utility/Collateral/App/Model/Collateral';
import { OperatorConfiguration as CollateralOperatorConfiguration } from '@daml.js/utility-collateral-app-v1/lib/Utility/Collateral/App/Model/Configuration/Operator';
import { CollateralState } from '@daml.js/utility-collateral-app-v1/lib/Utility/Collateral/App/Model/State/module';
import {
  UserServiceRequest as CollateralUserServiceRequest,
  UserService as CollateralUserService,
} from '@daml.js/utility-collateral-app-v1/lib/Utility/Collateral/App/Service/User';
import {
  CommercialAgreement,
  CommercialAgreement_LockCoin_Result,
  CommercialAgreement_Modify_Result,
  CommercialAgreement_Revoke_Result,
} from '@daml.js/utility-commercials-v0/lib/Utility/Commercials/V0/Model/CommercialAgreement';
import {
  CommercialAgreementOffer,
  CommercialAgreementOffer_Accept_Result,
  CommercialAgreementOffer_Cancel_Result,
  CommercialAgreementOffer_Reject_Result,
} from '@daml.js/utility-commercials-v0/lib/Utility/Commercials/V0/Model/Offer';
import { UtilityFees } from '@daml.js/utility-commercials-v0/lib/Utility/Commercials/V0/Model/Types/module';
import {
  CredentialBilling,
  CredentialBilling_Bill_Result,
  CredentialBilling_Cancel_Result,
} from '@daml.js/utility-credential-app-v0/lib/Utility/Credential/App/V0/Model/Billing';
import {
  CredentialOffer,
  CredentialOffer_AcceptFree_Result,
  CredentialOffer_Reject_Result,
  CredentialOffer_Cancel_Result,
  CredentialOffer_AcceptPaid_Result,
} from '@daml.js/utility-credential-app-v0/lib/Utility/Credential/App/V0/Model/Offer';
import {
  UserService,
  UserService_AcceptFreeCredentialOffer,
  UserService_CancelCredentialOffer,
  UserService_OfferFreeCredential_Result,
  UserService_OfferPaidCredential_Result,
  UserService_RejectCredentialOffer,
  UserServiceRequest,
  UserServiceRequest_Accept_Result,
} from '@daml.js/utility-credential-app-v0/lib/Utility/Credential/App/V0/Service/User';
import { BillingParams } from '@daml.js/utility-credential-app-v0/lib/Utility/Credential/App/V0/Types';
import {
  Claim,
  Credential,
  PartyCredentialRequirement,
} from '@daml.js/utility-credential-v0/lib/Utility/Credential/V0/Credential';
import { OperatorConfiguration } from '@daml.js/utility-registry-app-v0/lib/Utility/Registry/App/V0/Configuration/Operator';
import { ProviderConfiguration } from '@daml.js/utility-registry-app-v0/lib/Utility/Registry/App/V0/Configuration/Provider';
import {
  BurnRequest as BurnRequestTwoStep,
  RejectedBurn as RejectedBurnTwoStep,
  ExecutedBurn as ExecutedBurnTwoStep,
} from '@daml.js/utility-registry-app-v0/lib/Utility/Registry/App/V0/Model/Burn';
import {
  MintRequest as MintRequestTwoStep,
  RejectedMint as RejectedMintTwoStep,
  ExecutedMint as ExecutedMintTwoStep,
} from '@daml.js/utility-registry-app-v0/lib/Utility/Registry/App/V0/Model/Mint';
import { AllocationFactory } from '@daml.js/utility-registry-app-v0/lib/Utility/Registry/App/V0/Service/AllocationFactory';
import {
  HolderService,
  HolderService_AcceptTransferRequest,
  HolderService_CancelTransferRequest,
  HolderService_RejectTransferRequest,
  HolderService_AcceptTransferOffer,
  HolderService_CancelTransferOffer,
  HolderService_OfferTransfer,
  HolderService_RejectTransferOffer,
  HolderServiceRequest,
  HolderService_OfferTransfer_Result,
} from '@daml.js/utility-registry-app-v0/lib/Utility/Registry/App/V0/Service/Holder';
import {
  ProviderService,
  ProviderService_CreateProviderConfiguration_Result,
  ProviderServiceRequest,
} from '@daml.js/utility-registry-app-v0/lib/Utility/Registry/App/V0/Service/Provider';
import {
  RegistrarService,
  RegistrarService_CreateInstrumentConfiguration_Result,
  RegistrarServiceRequest,
} from '@daml.js/utility-registry-app-v0/lib/Utility/Registry/App/V0/Service/Registrar';
import { Holding } from '@daml.js/utility-registry-holding-v0/lib/Utility/Registry/Holding/V0/Holding';
import { InstrumentIdentifier } from '@daml.js/utility-registry-holding-v0/lib/Utility/Registry/Holding/V0/Types';
import {
  AppRewardConfiguration,
  AppRewardConfigurationDetails,
} from '@daml.js/utility-registry-v0/lib/Utility/Registry/V0/Configuration/AppReward';
import { InstrumentConfiguration } from '@daml.js/utility-registry-v0/lib/Utility/Registry/V0/Configuration/Instrument';
import {
  AcceptedBurn,
  BurnOffer,
  BurnRequest,
  ExecutedBurn,
  FailedBurn,
  RejectedBurn,
} from '@daml.js/utility-registry-v0/lib/Utility/Registry/V0/Holding/Burn';
import {
  AcceptedLock,
  ExecutedLock,
  FailedLock,
  LockOffer,
  LockRequest,
  RejectedLock,
} from '@daml.js/utility-registry-v0/lib/Utility/Registry/V0/Holding/Lock';
import {
  AcceptedMint,
  ExecutedMint,
  FailedMint,
  MintOffer,
  MintRequest,
  RejectedMint,
} from '@daml.js/utility-registry-v0/lib/Utility/Registry/V0/Holding/Mint';
import {
  TransferOffer,
  TransferRequest,
  TransferOffer_Accept_Result,
  TransferOffer_Reject_Result,
  TransferOffer_Cancel_Result,
  TransferRequest_Accept_Result,
  TransferRequest_Reject_Result,
  TransferRequest_Cancel_Result,
  AcceptedTransfer,
  RejectedTransfer,
  ExecutedTransfer,
  FailedTransfer,
} from '@daml.js/utility-registry-v0/lib/Utility/Registry/V0/Holding/Transfer';
import {
  AcceptedUnlock,
  ExecutedUnlock,
  FailedUnlock,
  RejectedUnlock,
  UnlockOffer,
  UnlockRequest,
} from '@daml.js/utility-registry-v0/lib/Utility/Registry/V0/Holding/Unlock';
import { TransferRule } from '@daml.js/utility-registry-v0/lib/Utility/Registry/V0/Rule/Transfer';
import { Batch } from '@daml.js/utility-registry-v0/lib/Utility/Registry/V0/Types';
import { CreateEvent, ArchiveEvent, CommandMeta } from '@daml/ledger';
import {
  Choice,
  ContractId,
  DisclosedContract as DamlDisclosedContract,
  InterfaceCompanion,
  Optional,
  Party,
  Template,
} from '@daml/types';
import { PackageName } from '../config/package';
import { ACSHelper } from './ACSHelper';
import InterfaceContract from './InterfaceContract';
import TemplateContract from './TemplateContract';
import { WSSupport } from './WSSupport';

type SubmitterArgs =
  | {
      actAs?: Party[];
      readAs?: Party[];
      submitter?: never;
    }
  | {
      actAs?: never;
      readAs?: never;
      submitter?: Party;
    };

export type LedgerCreateArgs<T extends object> = {
  argument: T;
  meta?: CommandMeta;
} & SubmitterArgs;

export type LedgerExerciseArgs<T extends object, C> = {
  cid: ContractId<T>;
  argument: C;
  meta?: CommandMeta;
} & SubmitterArgs;

// Uses the JSON API (via @daml/ledger) to connect to the ledger.
export default class LedgerApiClientV2 {
  private apiInstance: DefaultApi;

  private userId: string;

  private defaultSubmitter: string;

  private deduplicationDurationInSeconds: number;

  // needs to be marked with # in order to avoid serialization (LedgerApiClientV2 is serialized when used as key)
  #acsHelper: ACSHelper;

  constructor(
    apiInstance: DefaultApi,
    userId: string,
    defaultParty: string,
    deduplicationDurationInSeconds: number,
    wsSupport: WSSupport,
  ) {
    this.apiInstance = apiInstance;
    this.userId = userId;
    this.deduplicationDurationInSeconds = deduplicationDurationInSeconds;

    this.defaultSubmitter = defaultParty;
    this.#acsHelper = new ACSHelper(this.apiInstance, wsSupport);
  }

  async getPreferredPackageVersion(packageName: PackageName, operator: string) {
    const packageVersion = await this.apiInstance
      .getV2InteractiveSubmissionPreferredPackageVersion(packageName, [
        operator,
        this.defaultSubmitter,
      ])
      .catch((error) => {
        console.warn('No package detected:', error);
        return undefined;
      });
    return packageVersion;
  }

  setDefaultSubmitter(party: string): void {
    this.defaultSubmitter = party;
  }

  api() {
    return this.apiInstance;
  }

  async offerCommercialAgreement(
    operator: string,
    user: string,
    feeReceiver: string,
    utilityFees: UtilityFees,
    dso: string,
    initialDepositAmountCc: Optional<string>,
    rewardReceiver: Optional<string>,
  ): Promise<CreateEvent<CommercialAgreementOffer>> {
    return this.create(CommercialAgreementOffer, {
      operator,
      user,
      feeReceiver,
      utilityFees,
      dso,
      initialDepositAmountCc,
      rewardReceiver,
    });
  }

  async rejectCommercialAgreementOffer(
    commercialAgreementOfferCid: ContractId<CommercialAgreementOffer>,
    reason: string,
  ): Promise<CommercialAgreementOffer_Reject_Result> {
    return this.exercise(
      CommercialAgreementOffer.CommercialAgreementOffer_Reject,
      commercialAgreementOfferCid,
      { reason },
    );
  }

  async modifyCommercialAgreement(
    commercialAgreementCid: ContractId<CommercialAgreement>,
    feeReceiver: string,
    utilityFees: UtilityFees,
    rewardReceiver: Optional<string>,
  ): Promise<CommercialAgreement_Modify_Result> {
    return this.exercise(CommercialAgreement.CommercialAgreement_Modify, commercialAgreementCid, {
      feeReceiver,
      utilityFees,
      rewardReceiver,
    });
  }

  async revokeCommercialAgreement(
    commercialAgreementCid: ContractId<CommercialAgreement>,
    transferContext: AppTransferContext,
    transferPreapprovalCid: Optional<ContractId<TransferPreapproval>>,
    actor: string,
    disclosedContracts: DamlDisclosedContract[],
  ): Promise<CommercialAgreement_Revoke_Result> {
    const argument = {
      transferContext,
      actor,
      transferPreapprovalCid,
    };
    return this.exercise(
      CommercialAgreement.CommercialAgreement_Revoke,
      commercialAgreementCid,
      argument,
      { disclosedContracts },
    );
  }

  async queryTransferInstructions(
    offset: number,
  ): Promise<InterfaceContract<TransferInstruction>[]> {
    return this.queryInterface(offset, TransferInstruction);
  }

  async acceptCommercialAgreementOffer(
    commercialAgreementOfferCid: ContractId<CommercialAgreementOffer>,
    operator: string,
    user: string,
    feeReceiver: string,
    utilityFees: UtilityFees,
    dso: string,
  ): Promise<CommercialAgreementOffer_Accept_Result> {
    const payload = {
      operator,
      user,
      feeReceiver,
      utilityFees,
      dso,
    };
    return this.exercise(
      CommercialAgreementOffer.CommercialAgreementOffer_Accept,
      commercialAgreementOfferCid,
      payload,
      undefined,
    );
  }

  async cancelCommercialAgreementOffer(
    commercialAgreementOfferCid: ContractId<CommercialAgreementOffer>,
  ): Promise<CommercialAgreementOffer_Cancel_Result> {
    return this.exercise(
      CommercialAgreementOffer.CommercialAgreementOffer_Cancel,
      commercialAgreementOfferCid,
      {},
      undefined,
    );
  }

  async commercialAgreementLockCoin(
    commercialAgreementCid: ContractId<CommercialAgreement>,
    targetAmount: string,
    coinCids: ContractId<Amulet>[],
    transferContext: AppTransferContext,
    disclosedContracts: DamlDisclosedContract[],
  ): Promise<CommercialAgreement_LockCoin_Result> {
    const payload = {
      targetAmount,
      coinCids,
      transferContext,
    };
    return this.exercise(
      CommercialAgreement.CommercialAgreement_LockCoin,
      commercialAgreementCid,
      payload,
      { disclosedContracts },
      undefined,
    );
  }

  async createCredentialUserServiceRequest(
    operator: string,
    user: string,
  ): Promise<CreateEvent<UserServiceRequest>> {
    return this.create(UserServiceRequest, { operator, user }, undefined, [user], []);
  }

  async acceptCredentialUserServiceRequest(
    userServiceRequestCid: ContractId<UserServiceRequest>,
    dso: string,
  ): Promise<UserServiceRequest_Accept_Result> {
    return this.exercise(UserServiceRequest.UserServiceRequest_Accept, userServiceRequestCid, {
      dso,
    });
  }

  async queryCollateralAgreementChangeRequests(
    offset: number,
  ): Promise<TemplateContract<CollateralAgreementChangeRequest>[]> {
    const res = this.query(offset, CollateralAgreementChangeRequest);
    return res;
  }

  async queryBurnRequestsTwoStep(offset: number): Promise<TemplateContract<BurnRequestTwoStep>[]> {
    const res = this.query(offset, BurnRequestTwoStep);
    return res;
  }

  async queryExecutedBurnsTwoStep(
    offset: number,
  ): Promise<TemplateContract<ExecutedBurnTwoStep>[]> {
    const res = this.query(offset, ExecutedBurnTwoStep);
    return res;
  }

  async queryRejectedBurnsTwoStep(
    offset: number,
  ): Promise<TemplateContract<RejectedBurnTwoStep>[]> {
    const res = this.query(offset, RejectedBurnTwoStep);
    return res;
  }

  async queryMintRequestsTwoStep(offset: number): Promise<TemplateContract<MintRequestTwoStep>[]> {
    const res = this.query(offset, MintRequestTwoStep);
    return res;
  }

  async queryExecutedMintsTwoStep(
    offset: number,
  ): Promise<TemplateContract<ExecutedMintTwoStep>[]> {
    const res = this.query(offset, ExecutedMintTwoStep);
    return res;
  }

  async queryRejectedMintsTwoStep(
    offset: number,
  ): Promise<TemplateContract<RejectedMintTwoStep>[]> {
    const res = this.query(offset, RejectedMintTwoStep);
    return res;
  }

  async rejectCredentialUserServiceRequest(
    userServiceRequestCid: ContractId<UserServiceRequest>,
  ): Promise<void> {
    await this.exercise(UserServiceRequest.UserServiceRequest_Reject, userServiceRequestCid, {});
  }

  async cancelCredentialUserServiceRequest(
    userServiceRequestCid: ContractId<UserServiceRequest>,
  ): Promise<void> {
    await this.exercise(UserServiceRequest.UserServiceRequest_Cancel, userServiceRequestCid, {});
  }

  async terminateCredentialUserService(
    userServiceCid: ContractId<UserService>,
    actor: string,
  ): Promise<void> {
    await this.exercise(UserService.UserService_Terminate, userServiceCid, { actor });
  }

  async offerFreeCredential(
    userServiceCid: ContractId<UserService>,
    holder: string,
    id: string,
    description: string,
    claims: Claim[],
  ): Promise<UserService_OfferFreeCredential_Result> {
    return this.exercise(UserService.UserService_OfferFreeCredential, userServiceCid, {
      holder,
      id,
      description,
      claims,
    });
  }

  async offerPaidCredential(
    userServiceCid: ContractId<UserService>,
    holder: string,
    id: string,
    description: string,
    claims: Claim[],
    billingParams: BillingParams,
    depositInitialAmountUsd: string,
  ): Promise<UserService_OfferPaidCredential_Result> {
    return this.exercise(UserService.UserService_OfferPaidCredential, userServiceCid, {
      holder,
      id,
      description,
      claims,
      billingParams,
      depositInitialAmountUsd,
    });
  }

  async acceptPaidCredentialOffer(
    userServiceCid: ContractId<UserService>,
    credentialOfferCid: ContractId<CredentialOffer>,
    depositAmulets: ContractId<Amulet>[],
    appTransferContext: AppTransferContext,
    disclosedContracts: DamlDisclosedContract[],
  ): Promise<CredentialOffer_AcceptPaid_Result> {
    return this.exercise(
      UserService.UserService_AcceptPaidCredentialOffer,
      userServiceCid,
      { credentialOfferCid, depositAmulets, appTransferContext },
      { disclosedContracts },
    );
  }

  async distributeAmulet(
    userServiceCid: ContractId<UserService>,
    credentialBillingCid: ContractId<CredentialBilling>,
    amountUsd: string,
    coinCids: ContractId<Amulet>[],
    appTransferContext: AppTransferContext,
    disclosedContracts: DamlDisclosedContract[],
  ): Promise<void> {
    await this.exercise(
      UserService.UserService_DistributeAndAdjustDeposit,
      userServiceCid,
      {
        credentialBillingCid,
        amountUsd,
        coinCids,
        appTransferContext,
      },
      { disclosedContracts },
    );
  }

  async topupLockedCredentialDeposit(
    userServiceCid: ContractId<UserService>,
    credentialBillingCid: ContractId<CredentialBilling>,
    amountUsd: string,
    coinCids: ContractId<Amulet>[],
    appTransferContext: AppTransferContext,
    disclosedContracts: DamlDisclosedContract[],
  ): Promise<void> {
    await this.exercise(
      UserService.UserService_TopUp,
      userServiceCid,
      {
        credentialBillingCid,
        amountUsd,
        coinCids,
        appTransferContext,
      },
      { disclosedContracts },
    );
  }

  async billCredential(
    credentialBillingCid: ContractId<CredentialBilling>,
    appTransferContext: AppTransferContext,
    disclosedContracts: DamlDisclosedContract[],
  ): Promise<CredentialBilling_Bill_Result> {
    const enableFeeRecord = null;
    const rewardReceiver = null;
    const featuredAppRightCid = null;
    return this.exercise(
      CredentialBilling.CredentialBilling_Bill,
      credentialBillingCid,
      { appTransferContext, enableFeeRecord, rewardReceiver, featuredAppRightCid },
      { disclosedContracts },
    );
  }

  async revokeCredential(
    userServiceCid: ContractId<UserService>,
    credentialCid: ContractId<Credential>,
  ): Promise<void> {
    await this.exercise(UserService.UserService_RevokeCredential, userServiceCid, {
      credentialCid,
    });
  }

  async cancelCredentialBilling(
    credentialUserServiceCid: ContractId<UserService>,
    credentialBillingCid: ContractId<CredentialBilling>,
    appTransferContext: AppTransferContext,
    disclosedContracts: DamlDisclosedContract[],
  ): Promise<CredentialBilling_Cancel_Result> {
    return this.exercise(
      UserService.UserService_CancelCredentialBilling,
      credentialUserServiceCid,
      { credentialBillingCid, appTransferContext },
      { disclosedContracts },
    );
  }

  async revokeCredentialAndCancelBilling(
    userServiceCid: ContractId<UserService>,
    credentialCid: ContractId<Credential>,
    credentialBillingCid: ContractId<CredentialBilling>,
    appTransferContext: AppTransferContext,
    disclosedContracts: DamlDisclosedContract[],
  ): Promise<void> {
    await this.exercise(
      UserService.UserService_RevokeCredentialAndCancelBilling,
      userServiceCid,
      { credentialCid, credentialBillingCid, appTransferContext },
      { disclosedContracts },
    );
  }

  async requestRegistryProviderService(
    operator: string,
    provider: string,
  ): Promise<CreateEvent<ProviderServiceRequest>> {
    return this.create(ProviderServiceRequest, {
      operator,
      provider,
    });
  }

  async terminateRegistryProviderService(
    providerServiceCid: ContractId<ProviderService>,
    actor: string,
  ): Promise<void> {
    await this.exercise(ProviderService.ProviderService_Terminate, providerServiceCid, {
      actor,
    });
  }

  async requestRegistryRegistrarService(
    operator: string,
    provider: string,
    registrar: string,
  ): Promise<CreateEvent<RegistrarServiceRequest>> {
    return this.create(RegistrarServiceRequest, {
      operator,
      provider,
      registrar,
    });
  }

  async terminateRegistryRegistrarService(
    registrarServiceCid: ContractId<RegistrarService>,
    actor: string,
  ): Promise<void> {
    await this.exercise(RegistrarService.RegistrarService_Terminate, registrarServiceCid, {
      actor,
    });
  }

  async terminateRegistryHolderService(
    holderServiceCid: ContractId<HolderService>,
    actor: string,
  ): Promise<void> {
    await this.exercise(HolderService.HolderService_Terminate, holderServiceCid, {
      actor,
    });
  }

  async acceptRegistryProviderServiceRequest(
    providerServiceRequestCid: ContractId<ProviderServiceRequest>,
    operatorConfigurationCid: ContractId<OperatorConfiguration>,
    credentialCids: ContractId<Credential>[],
    appRewardConfigurationDetails: Optional<AppRewardConfigurationDetails>,
  ): Promise<void> {
    await this.exercise(
      ProviderServiceRequest.ProviderServiceRequest_Accept,
      providerServiceRequestCid,
      { operatorConfigurationCid, credentialCids, appRewardConfigurationDetails },
    );
  }

  async rejectRegistryProviderServiceRequest(
    providerServiceRequestCid: ContractId<ProviderServiceRequest>,
    reason: string,
  ): Promise<void> {
    await this.exercise(
      ProviderServiceRequest.ProviderServiceRequest_Reject,
      providerServiceRequestCid,
      { reason },
    );
  }

  async cancelRegistryProviderServiceRequest(
    providerServiceRequestCid: ContractId<ProviderServiceRequest>,
  ): Promise<void> {
    await this.exercise(
      ProviderServiceRequest.ProviderServiceRequest_Cancel,
      providerServiceRequestCid,
      {},
    );
  }

  async acceptRegistryRegistrarServiceRequest(
    registrarServiceRequestCid: ContractId<RegistrarServiceRequest>,
    providerConfigurationCid: ContractId<ProviderConfiguration>,
    credentialCids: ContractId<Credential>[],
  ): Promise<void> {
    await this.exercise(
      RegistrarServiceRequest.RegistrarServiceRequest_Accept,
      registrarServiceRequestCid,
      { providerConfigurationCid, credentialCids },
    );
  }

  async rejectRegistryRegistrarServiceRequest(
    registrarServiceRequestCid: ContractId<RegistrarServiceRequest>,
    reason: string,
  ): Promise<void> {
    await this.exercise(
      RegistrarServiceRequest.RegistrarServiceRequest_Reject,
      registrarServiceRequestCid,
      { reason },
    );
  }

  async cancelRegistryRegistrarServiceRequest(
    registrarServiceRequestCid: ContractId<RegistrarServiceRequest>,
  ): Promise<void> {
    await this.exercise(
      RegistrarServiceRequest.RegistrarServiceRequest_Cancel,
      registrarServiceRequestCid,
      {},
    );
  }

  async acceptRegistryHolderServiceRequest(
    holderServiceRequestCid: ContractId<HolderServiceRequest>,
    providerConfigurationCid: ContractId<ProviderConfiguration>,
    credentialCids: ContractId<Credential>[],
  ): Promise<void> {
    await this.exercise(HolderServiceRequest.HolderServiceRequest_Accept, holderServiceRequestCid, {
      providerConfigurationCid,
      credentialCids,
    });
  }

  async rejectRegistryHolderServiceRequest(
    holderServiceRequestCid: ContractId<HolderServiceRequest>,
    reason: string,
  ): Promise<void> {
    await this.exercise(HolderServiceRequest.HolderServiceRequest_Reject, holderServiceRequestCid, {
      reason,
    });
  }

  async cancelRegistryHolderServiceRequest(
    holderServiceRequestCid: ContractId<HolderServiceRequest>,
  ): Promise<void> {
    await this.exercise(
      HolderServiceRequest.HolderServiceRequest_Cancel,
      holderServiceRequestCid,
      {},
    );
  }

  async modifyRegistryOperatorConfiguration(
    operatorConfigurationCid: ContractId<OperatorConfiguration>,
    newProviderRequirements: PartyCredentialRequirement[],
  ): Promise<ContractId<OperatorConfiguration>> {
    return this.exercise(
      OperatorConfiguration.OperatorConfiguration_Modify,
      operatorConfigurationCid,
      { newProviderRequirements },
    ).then((res) => res.operatorConfigurationCid);
  }

  async archiveRegistryOperatorConfiguration(
    operatorConfigurationCid: ContractId<OperatorConfiguration>,
  ): Promise<ArchiveEvent<OperatorConfiguration>> {
    return this.exercise(OperatorConfiguration.Archive, operatorConfigurationCid, {}).then(() => ({
      templateId: OperatorConfiguration.templateId,
      contractId: operatorConfigurationCid,
    }));
  }

  async createRegistryOperatorConfiguration(
    operator: string,
    providerRequirements: PartyCredentialRequirement[],
  ): Promise<CreateEvent<OperatorConfiguration>> {
    return this.create(OperatorConfiguration, { operator, providerRequirements });
  }

  async createRegistryProviderConfiguration(
    providerServiceCid: ContractId<ProviderService>,
    holderRequirements: PartyCredentialRequirement[],
    registrarRequirements: PartyCredentialRequirement[],
  ): Promise<ContractId<ProviderConfiguration>> {
    return this.exercise(
      ProviderService.ProviderService_CreateProviderConfiguration,
      providerServiceCid,
      { holderRequirements, registrarRequirements },
    ).then((res) => res.providerConfigurationCid);
  }

  async archiveRegistryProviderConfiguration(
    providerServiceCid: ContractId<ProviderService>,
    providerConfigurationCid: ContractId<ProviderConfiguration>,
  ): Promise<void> {
    await this.exercise(
      ProviderService.ProviderService_ArchiveProviderConfiguration,
      providerServiceCid,
      { cid: providerConfigurationCid },
    );
  }

  async archiveAndCreateRegistryProviderConfiguration(
    providerServiceCid: ContractId<ProviderService>,
    providerConfigurationCid: ContractId<ProviderConfiguration>,
    holderRequirements: PartyCredentialRequirement[],
    registrarRequirements: PartyCredentialRequirement[],
  ): Promise<ProviderService_CreateProviderConfiguration_Result> {
    return this.exercise(
      ProviderService.ProviderService_ArchiveAndCreateProviderConfiguration,
      providerServiceCid,
      { cid: providerConfigurationCid, payload: { holderRequirements, registrarRequirements } },
    ).then((res) => res.createResult);
  }

  async createRegistryInstrumentConfiguration(
    registryServiceCid: ContractId<RegistrarService>,
    instrumentId: string,
    additionalIdentifiers: InstrumentIdentifier[],
    issuerRequirements: PartyCredentialRequirement[],
    holderRequirements: PartyCredentialRequirement[],
  ): Promise<ContractId<InstrumentConfiguration>> {
    return this.exercise(
      RegistrarService.RegistrarService_CreateInstrumentConfiguration,
      registryServiceCid,
      {
        instrumentId,
        additionalIdentifiers,
        issuerRequirements,
        holderRequirements,
      },
    ).then((res) => res.instrumentConfigurationCid);
  }

  async archiveRegistryInstrumentConfiguration(
    registryServiceCid: ContractId<RegistrarService>,
    instrumentConfigurationCid: ContractId<InstrumentConfiguration>,
  ): Promise<void> {
    await this.exercise(
      RegistrarService.RegistrarService_ArchiveInstrumentConfiguration,
      registryServiceCid,
      { cid: instrumentConfigurationCid },
    );
  }

  async archiveAndCreateRegistryInstrumentConfiguration(
    registryServiceCid: ContractId<RegistrarService>,
    instrumentConfigurationCid: ContractId<InstrumentConfiguration>,
    instrumentId: string,
    additionalIdentifiers: InstrumentIdentifier[],
    issuerRequirements: PartyCredentialRequirement[],
    holderRequirements: PartyCredentialRequirement[],
  ): Promise<RegistrarService_CreateInstrumentConfiguration_Result> {
    return this.exercise(
      RegistrarService.RegistrarService_ArchiveAndCreateInstrumentConfiguration,
      registryServiceCid,
      {
        cid: instrumentConfigurationCid,
        payload: {
          instrumentId,
          additionalIdentifiers,
          issuerRequirements,
          holderRequirements,
        },
      },
    ).then((res) => res.createResult);
  }

  async requestMint(
    holderServiceCid: ContractId<HolderService>,
    registrar: string,
    instrumentIdentifier: InstrumentIdentifier,
    amount: string,
    holdingLabel: string,
    reference: string,
    batch: Batch,
  ): Promise<ContractId<MintRequest>> {
    return this.exercise(HolderService.HolderService_RequestMint, holderServiceCid, {
      registrar,
      instrumentIdentifier,
      amount,
      holdingLabel,
      reference,
      batch,
    }).then((res) => res.mintRequestCid);
  }

  async acceptMintRequest(
    registrarServiceCid: ContractId<RegistrarService>,
    mintRequestCid: ContractId<MintRequest>,
  ): Promise<ContractId<AcceptedMint>> {
    return this.exercise(RegistrarService.RegistrarService_AcceptMintRequest, registrarServiceCid, {
      cid: mintRequestCid,
      payload: {},
    }).then((res) => res.acceptedMintCid);
  }

  async rejectMintRequest(
    registrarServiceCid: ContractId<RegistrarService>,
    mintRequestCid: ContractId<MintRequest>,
    reason: string,
  ): Promise<ContractId<RejectedMint>> {
    return this.exercise(RegistrarService.RegistrarService_RejectMintRequest, registrarServiceCid, {
      cid: mintRequestCid,
      payload: { reason },
    }).then((res) => res.rejectedMintCid);
  }

  async cancelMintRequest(
    holderServiceCid: ContractId<HolderService>,
    mintRequestCid: ContractId<MintRequest>,
  ): Promise<void> {
    await this.exercise(HolderService.HolderService_CancelMintRequest, holderServiceCid, {
      cid: mintRequestCid,
      payload: {},
    });
  }

  async requestBurn(
    holderServiceCid: ContractId<HolderService>,
    registrar: string,
    instrumentIdentifier: InstrumentIdentifier,
    amount: string,
    holdingLabel: string,
    reference: string,
    batch: Batch,
  ): Promise<ContractId<BurnRequest>> {
    return this.exercise(HolderService.HolderService_RequestBurn, holderServiceCid, {
      registrar,
      instrumentIdentifier,
      amount,
      holdingLabel,
      reference,
      batch,
    }).then((res) => res.burnRequestCid);
  }

  async acceptBurnRequest(
    registrarServiceCid: ContractId<RegistrarService>,
    burnRequestCid: ContractId<BurnRequest>,
  ): Promise<ContractId<AcceptedBurn>> {
    return this.exercise(RegistrarService.RegistrarService_AcceptBurnRequest, registrarServiceCid, {
      cid: burnRequestCid,
      payload: {},
    }).then((res) => res.acceptedBurnCid);
  }

  async rejectBurnRequest(
    registrarServiceCid: ContractId<RegistrarService>,
    burnRequestCid: ContractId<BurnRequest>,
    reason: string,
  ): Promise<ContractId<RejectedBurn>> {
    return this.exercise(RegistrarService.RegistrarService_RejectBurnRequest, registrarServiceCid, {
      cid: burnRequestCid,
      payload: { reason },
    }).then((res) => res.rejectedBurnCid);
  }

  async cancelBurnRequest(
    holderServiceCid: ContractId<HolderService>,
    burnRequestCid: ContractId<BurnRequest>,
  ): Promise<void> {
    await this.exercise(HolderService.HolderService_CancelBurnRequest, holderServiceCid, {
      cid: burnRequestCid,
      payload: {},
    });
  }

  async offerLock(
    holderServiceCid: ContractId<HolderService>,
    registrar: string,
    locker: string,
    instrumentIdentifier: InstrumentIdentifier,
    amount: string,
    holdingLabel: string,
    context: string,
    reference: string,
    batch: Batch,
  ): Promise<ContractId<LockOffer>> {
    return this.exercise(HolderService.HolderService_OfferLock, holderServiceCid, {
      registrar,
      locker,
      instrumentIdentifier,
      amount,
      holdingLabel,
      context,
      reference,
      batch,
    }).then((res) => res.lockOfferCid);
  }

  async acceptLockOffer(
    holderServiceCid: ContractId<HolderService>,
    lockOfferCid: ContractId<LockOffer>,
  ): Promise<ContractId<AcceptedLock>> {
    return this.exercise(HolderService.HolderService_AcceptLockOffer, holderServiceCid, {
      cid: lockOfferCid,
      payload: {},
    }).then((res) => res.acceptedLockCid);
  }

  async rejectLockOffer(
    holderServiceCid: ContractId<HolderService>,
    lockOfferCid: ContractId<LockOffer>,
    reason: string,
  ): Promise<ContractId<RejectedLock>> {
    return this.exercise(HolderService.HolderService_RejectLockOffer, holderServiceCid, {
      cid: lockOfferCid,
      payload: { reason },
    }).then((res) => res.rejectedLockCid);
  }

  async cancelLockOffer(
    holderServiceCid: ContractId<HolderService>,
    lockOfferCid: ContractId<LockOffer>,
  ): Promise<void> {
    await this.exercise(HolderService.HolderService_CancelLockOffer, holderServiceCid, {
      cid: lockOfferCid,
      payload: {},
    });
  }

  async requestLock(
    holderServiceCid: ContractId<HolderService>,
    registrar: string,
    holder: string,
    instrumentIdentifier: InstrumentIdentifier,
    amount: string,
    context: string,
    reference: string,
    batch: Batch,
  ): Promise<ContractId<LockRequest>> {
    return this.exercise(HolderService.HolderService_RequestLock, holderServiceCid, {
      registrar,
      holder,
      instrumentIdentifier,
      amount,
      context,
      reference,
      batch,
    }).then((res) => res.lockRequestCid);
  }

  async acceptLockRequest(
    holderServiceCid: ContractId<HolderService>,
    lockRequestCid: ContractId<LockRequest>,
    holdingLabel: string,
  ): Promise<ContractId<AcceptedLock>> {
    return this.exercise(HolderService.HolderService_AcceptLockRequest, holderServiceCid, {
      cid: lockRequestCid,
      payload: { holdingLabel },
    }).then((res) => res.acceptedLockCid);
  }

  async rejectLockRequest(
    holderServiceCid: ContractId<HolderService>,
    lockRequestCid: ContractId<LockRequest>,
    reason: string,
  ): Promise<ContractId<RejectedLock>> {
    return this.exercise(HolderService.HolderService_RejectLockRequest, holderServiceCid, {
      cid: lockRequestCid,
      payload: { reason },
    }).then((res) => res.rejectedLockCid);
  }

  async cancelLockRequest(
    holderServiceCid: ContractId<HolderService>,
    lockRequestCid: ContractId<LockRequest>,
  ): Promise<void> {
    await this.exercise(HolderService.HolderService_CancelLockRequest, holderServiceCid, {
      cid: lockRequestCid,
      payload: {},
    });
  }

  async offerUnlock(
    holderServiceCid: ContractId<HolderService>,
    registrar: string,
    locker: string,
    lockContext: string,
    instrumentIdentifier: InstrumentIdentifier,
    amount: string,
    holdingLabel: string,
    reference: string,
    batch: Batch,
  ): Promise<ContractId<UnlockOffer>> {
    return this.exercise(HolderService.HolderService_OfferUnlock, holderServiceCid, {
      registrar,
      locker,
      lockContext,
      instrumentIdentifier,
      amount,
      holdingLabel,
      reference,
      batch,
    }).then((res) => res.unlockOfferCid);
  }

  async acceptUnlockOffer(
    holderServiceCid: ContractId<HolderService>,
    unlockOfferCid: ContractId<UnlockOffer>,
  ): Promise<ContractId<AcceptedUnlock>> {
    return this.exercise(HolderService.HolderService_AcceptUnlockOffer, holderServiceCid, {
      cid: unlockOfferCid,
      payload: {},
    }).then((res) => res.acceptedUnlockCid);
  }

  async rejectUnlockOffer(
    holderServiceCid: ContractId<HolderService>,
    unlockOfferCid: ContractId<UnlockOffer>,
    reason: string,
  ): Promise<ContractId<RejectedUnlock>> {
    return this.exercise(HolderService.HolderService_RejectUnlockOffer, holderServiceCid, {
      cid: unlockOfferCid,
      payload: { reason },
    }).then((res) => res.rejectedUnlockCid);
  }

  async cancelUnlockOffer(
    holderServiceCid: ContractId<HolderService>,
    unlockOfferCid: ContractId<UnlockOffer>,
  ): Promise<void> {
    await this.exercise(HolderService.HolderService_CancelUnlockOffer, holderServiceCid, {
      cid: unlockOfferCid,
      payload: {},
    });
  }

  async requestUnlock(
    holderServiceCid: ContractId<HolderService>,
    registrar: string,
    lockContext: string,
    holder: string,
    instrumentIdentifier: InstrumentIdentifier,
    amount: string,
    holdingLabel: string,
    reference: string,
    batch: Batch,
  ): Promise<ContractId<UnlockRequest>> {
    return this.exercise(HolderService.HolderService_RequestUnlock, holderServiceCid, {
      registrar,
      holder,
      lockContext,
      instrumentIdentifier,
      amount,
      holdingLabel,
      reference,
      batch,
    }).then((res) => res.unlockRequestCid);
  }

  async acceptUnlockRequest(
    holderServiceCid: ContractId<HolderService>,
    unlockRequestCid: ContractId<UnlockRequest>,
    holdingLabel: string,
  ): Promise<ContractId<AcceptedUnlock>> {
    return this.exercise(HolderService.HolderService_AcceptUnlockRequest, holderServiceCid, {
      cid: unlockRequestCid,
      payload: { holdingLabel },
    }).then((res) => res.acceptedUnlockCid);
  }

  async rejectUnlockRequest(
    holderServiceCid: ContractId<HolderService>,
    unlockRequestCid: ContractId<UnlockRequest>,
    reason: string,
  ): Promise<ContractId<RejectedUnlock>> {
    return this.exercise(HolderService.HolderService_RejectUnlockRequest, holderServiceCid, {
      cid: unlockRequestCid,
      payload: { reason },
    }).then((res) => res.rejectedUnlockCid);
  }

  async cancelUnlockRequest(
    holderServiceCid: ContractId<HolderService>,
    unlockRequestCid: ContractId<UnlockRequest>,
  ): Promise<void> {
    await this.exercise(HolderService.HolderService_CancelUnlockRequest, holderServiceCid, {
      cid: unlockRequestCid,
      payload: {},
    });
  }

  async tapCoin(
    amuletRulesCid: ContractId<AmuletRules>,
    receiver: string,
    amount: string,
    openRound: ContractId<OpenMiningRound>,
    disclosedContracts: DamlDisclosedContract[],
  ): Promise<AmuletRules_DevNet_TapResult> {
    return this.exercise(
      AmuletRules.AmuletRules_DevNet_Tap,
      amuletRulesCid,
      { receiver, amount, openRound },
      { disclosedContracts },
    );
  }

  async calculateFees(
    amuletRulesCid: ContractId<AmuletRules>,
    context: TransferContext,
    sender: string,
    outputs: [TransferOutput],
    expectedDso: Optional<string>,
    disclosedContracts: DamlDisclosedContract[],
    latestAmuletPrice: string,
  ) {
    const res = await this.exercise(
      AmuletRules.AmuletRules_ComputeFees,
      amuletRulesCid,
      {
        context,
        sender,
        outputs,
        expectedDso,
      },
      { disclosedContracts },
    );
    return { res, latestAmuletPrice };
  }

  async requestRegistryHolderService(
    operator: string,
    provider: string,
    holder: string,
  ): Promise<CreateEvent<HolderServiceRequest>> {
    return this.create(
      HolderServiceRequest,
      {
        operator,
        provider,
        holder,
      },
      undefined,
      [holder],
      [],
    );
  }

  async acceptFreeCredential(
    userServiceCid: ContractId<UserService>,
    credentialOfferCid: ContractId<CredentialOffer>,
    submitters: Party[],
  ): Promise<CredentialOffer_AcceptFree_Result> {
    const argument: UserService_AcceptFreeCredentialOffer = {
      credentialOfferCid,
    };

    return this.exercise(
      UserService.UserService_AcceptFreeCredentialOffer,
      userServiceCid,
      argument,
      undefined,
      submitters,
    );
  }

  async rejectFreeCredential(
    userServiceCid: ContractId<UserService>,
    credentialOfferCid: ContractId<CredentialOffer>,
    reason: string,
    submitters: Party[],
  ): Promise<CredentialOffer_Reject_Result> {
    const argument: UserService_RejectCredentialOffer = {
      reason,
      credentialOfferCid,
    };

    return this.exercise(
      UserService.UserService_RejectCredentialOffer,
      userServiceCid,
      argument,
      undefined,
      submitters,
    );
  }

  async cancelFreeCredential(
    userServiceCid: ContractId<UserService>,
    credentialOfferCid: ContractId<CredentialOffer>,
    submitters: Party[],
  ): Promise<CredentialOffer_Cancel_Result> {
    const arg: UserService_CancelCredentialOffer = {
      credentialOfferCid,
    };

    return this.exercise(
      UserService.UserService_CancelCredentialOffer,
      userServiceCid,
      arg,
      undefined,
      submitters,
    );
  }

  async offerTransfer(
    holderServiceCid: ContractId<HolderService>,
    registrar: string,
    receiver: string,
    instrumentIdentifier: InstrumentIdentifier,
    amount: string,
    senderLabel: string,
    reference: string,
    batch: Batch,
    submitters: Party[],
  ): Promise<HolderService_OfferTransfer_Result> {
    const argument: HolderService_OfferTransfer = {
      registrar,
      receiver,
      instrumentIdentifier,
      amount,
      senderLabel,
      reference,
      batch,
    };
    return this.exercise(
      HolderService.HolderService_OfferTransfer,
      holderServiceCid,
      argument,
      undefined,
      submitters,
    );
  }

  async acceptTransferOffer(
    holderServiceCid: ContractId<HolderService>,
    transferOfferCid: ContractId<TransferOffer>,
    receiverLabel: string,
    submitters: Party[],
  ): Promise<TransferOffer_Accept_Result> {
    const argument: HolderService_AcceptTransferOffer = {
      cid: transferOfferCid,
      payload: { receiverLabel },
    };
    return this.exercise(
      HolderService.HolderService_AcceptTransferOffer,
      holderServiceCid,
      argument,
      undefined,
      submitters,
    );
  }

  async rejectTransferOffer(
    holderServiceCid: ContractId<HolderService>,
    transferOfferCid: ContractId<TransferOffer>,
    reason: string,
    submitters: Party[],
  ): Promise<TransferOffer_Reject_Result> {
    const argument: HolderService_RejectTransferOffer = {
      cid: transferOfferCid,
      payload: { reason },
    };
    return this.exercise(
      HolderService.HolderService_RejectTransferOffer,
      holderServiceCid,
      argument,
      undefined,
      submitters,
    );
  }

  async cancelTransferOffer(
    holderServiceCid: ContractId<HolderService>,
    transferOfferCid: ContractId<TransferOffer>,
    submitters: Party[],
  ): Promise<TransferOffer_Cancel_Result> {
    const argument: HolderService_CancelTransferOffer = {
      cid: transferOfferCid,
      payload: {},
    };

    return this.exercise(
      HolderService.HolderService_CancelTransferOffer,
      holderServiceCid,
      argument,
      undefined,
      submitters,
    );
  }

  async acceptTransferRequest(
    holderServiceCid: ContractId<HolderService>,
    transferRequestCid: ContractId<TransferRequest>,
    senderLabel: string,
    submitters: Party[],
  ): Promise<TransferRequest_Accept_Result> {
    const argument: HolderService_AcceptTransferRequest = {
      cid: transferRequestCid,
      payload: { senderLabel },
    };
    return this.exercise(
      HolderService.HolderService_AcceptTransferRequest,
      holderServiceCid,
      argument,
      undefined,
      submitters,
    );
  }

  async rejectTransferRequest(
    holderServiceCid: ContractId<HolderService>,
    transferRequestCid: ContractId<TransferRequest>,
    reason: string,
    submitters: Party[],
  ): Promise<TransferRequest_Reject_Result> {
    const argument: HolderService_RejectTransferRequest = {
      cid: transferRequestCid,
      payload: { reason },
    };
    return this.exercise(
      HolderService.HolderService_RejectTransferRequest,
      holderServiceCid,
      argument,
      undefined,
      submitters,
    );
  }

  async cancelTransferRequest(
    holderServiceCid: ContractId<HolderService>,
    transferRequestCid: ContractId<TransferRequest>,
    submitters: Party[],
  ): Promise<TransferRequest_Cancel_Result> {
    const argument: HolderService_CancelTransferRequest = {
      cid: transferRequestCid,
      payload: {},
    };
    return this.exercise(
      HolderService.HolderService_CancelTransferRequest,
      holderServiceCid,
      argument,
      undefined,
      submitters,
    );
  }

  async queryCredentialUserService(
    offset: number,
    operator: string,
    party: string,
  ): Promise<TemplateContract<UserService> | null> {
    const svcs = await this.query(offset, UserService);
    return svcs.find((r) => r.payload.operator === operator && r.payload.user === party) || null;
  }

  async queryCredentialUserServices(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<UserService>[]> {
    const svcs = await this.query(offset, UserService);
    return svcs.filter((r) => r.payload.operator === operator);
  }

  async queryCredentialUserServiceRequests(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<UserServiceRequest>[]> {
    const reqs = await this.query(offset, UserServiceRequest);
    return reqs.filter((r) => r.payload.operator === operator);
  }

  async queryCredentials(offset: number): Promise<TemplateContract<Credential>[]> {
    return this.query(offset, Credential);
  }

  async queryCredentialOffers(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<CredentialOffer>[]> {
    const svcs = await this.query(offset, CredentialOffer);
    return svcs.filter((r) => r.payload.operator === operator);
  }

  async queryCredentialBillings(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<CredentialBilling>[]> {
    const svcs = await this.query(offset, CredentialBilling);
    return svcs.filter((r) => r.payload.operator === operator);
  }

  async queryRegistryOperatorConfiguration(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<OperatorConfiguration> | null> {
    const configs = await this.query(offset, OperatorConfiguration);
    return configs.find((r) => r.payload.operator === operator) || null;
  }

  async queryRegistryOperatorConfigurations(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<OperatorConfiguration>[]> {
    const configs = await this.query(offset, OperatorConfiguration);
    return configs.filter((r) => r.payload.operator === operator);
  }

  async queryRegistryProviderConfiguration(
    offset: number,
    operator: string,
    provider: string,
  ): Promise<TemplateContract<ProviderConfiguration> | null> {
    const configs = await this.query(offset, ProviderConfiguration);
    return (
      configs.find((r) => r.payload.operator === operator && r.payload.provider === provider) ||
      null
    );
  }

  async queryRegistryProviderConfigurations(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<ProviderConfiguration>[]> {
    const configs = await this.query(offset, ProviderConfiguration);
    return configs.filter((r) => r.payload.operator === operator);
  }

  async queryRegistryInstrumentConfigurations(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<InstrumentConfiguration>[]> {
    const configs = await this.query(offset, InstrumentConfiguration);
    return configs.filter((r) => r.payload.operator === operator);
  }

  async queryRegistryProviderServiceRequests(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<ProviderServiceRequest>[]> {
    const reqs = await this.query(offset, ProviderServiceRequest);
    return reqs.filter((r) => r.payload.operator === operator);
  }

  async queryRegistryProviderServices(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<ProviderService>[]> {
    const reqs = await this.query(offset, ProviderService);
    return reqs.filter((r) => r.payload.operator === operator);
  }

  async queryRegistryProviderService(
    offset: number,
    operator: string,
    party: string,
  ): Promise<TemplateContract<ProviderService> | null> {
    const svcs = await this.query(offset, ProviderService);
    return (
      svcs.find((r) => r.payload.operator === operator && r.payload.provider === party) || null
    );
  }

  async queryRegistryRegistrarServiceRequests(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<RegistrarServiceRequest>[]> {
    const reqs = await this.query(offset, RegistrarServiceRequest);
    return reqs.filter((r) => r.payload.operator === operator);
  }

  async queryRegistryRegistrarServices(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<RegistrarService>[]> {
    const reqs = await this.query(offset, RegistrarService);
    return reqs.filter((r) => r.payload.operator === operator);
  }

  async queryRegistryRegistrarService(
    offset: number,
    operator: string,
    party: string,
  ): Promise<TemplateContract<RegistrarService> | null> {
    const svcs = await this.query(offset, RegistrarService);
    return (
      svcs.find((r) => r.payload.operator === operator && r.payload.registrar === party) || null
    );
  }

  async queryRegistryHolderServiceRequests(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<HolderServiceRequest>[]> {
    const reqs = await this.query(offset, HolderServiceRequest);
    return reqs.filter((r) => r.payload.operator === operator);
  }

  async queryRegistryHolderServices(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<HolderService>[]> {
    const reqs = await this.query(offset, HolderService);
    return reqs.filter((r) => r.payload.operator === operator);
  }

  async queryRegistryHolderService(
    offset: number,
    operator: string,
    party: string,
  ): Promise<TemplateContract<HolderService> | null> {
    const svcs = await this.query(offset, HolderService);
    return svcs.find((r) => r.payload.operator === operator && r.payload.holder === party) || null;
  }

  async queryHoldings(offset: number, operator: string): Promise<TemplateContract<Holding>[]> {
    const res = await this.query(offset, Holding);
    return res.filter(
      (h) =>
        h.payload.operator === operator &&
        (h.payload.owner === this.defaultSubmitter ||
          h.payload.registrar === this.defaultSubmitter ||
          h.payload.provider === this.defaultSubmitter ||
          h.payload.operator === this.defaultSubmitter),
    );
  }

  async queryAppRewardConfiguration(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<AppRewardConfiguration>[]> {
    const configs = await this.query(offset, AppRewardConfiguration);
    return configs.filter((r) => r.payload.operator === operator);
  }

  async queryInstrumentConfigurations(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<InstrumentConfiguration>[]> {
    const res = await this.query(offset, InstrumentConfiguration);
    return res.filter((r) => r.payload.operator === operator);
  }

  async queryMintRequests(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<MintRequest>[]> {
    const res = await this.query(offset, MintRequest);
    return res.filter((r) => r.payload.mint.operator === operator);
  }

  async queryMintOffers(offset: number, operator: string): Promise<TemplateContract<MintOffer>[]> {
    const res = await this.query(offset, MintOffer);
    return res.filter((r) => r.payload.mint.operator === operator);
  }

  async queryAcceptedMints(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<AcceptedMint>[]> {
    const res = await this.query(offset, AcceptedMint);
    return res.filter((r) => r.payload.mint.operator === operator);
  }

  async queryRejectedMints(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<RejectedMint>[]> {
    const res = await this.query(offset, RejectedMint);
    return res.filter((r) => r.payload.mint.operator === operator);
  }

  async queryExecutedMints(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<ExecutedMint>[]> {
    const res = await this.query(offset, ExecutedMint);
    return res.filter((r) => r.payload.mint.operator === operator);
  }

  async queryFailedMints(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<FailedMint>[]> {
    const res = await this.query(offset, FailedMint);
    return res.filter((r) => r.payload.mint.operator === operator);
  }

  async queryBurnRequests(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<BurnRequest>[]> {
    const res = await this.query(offset, BurnRequest);
    return res.filter((r) => r.payload.burn.operator === operator);
  }

  async queryBurnOffers(offset: number, operator: string): Promise<TemplateContract<BurnOffer>[]> {
    const res = await this.query(offset, BurnOffer);
    return res.filter((r) => r.payload.burn.operator === operator);
  }

  async queryAcceptedBurns(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<AcceptedBurn>[]> {
    const res = await this.query(offset, AcceptedBurn);
    return res.filter((r) => r.payload.burn.operator === operator);
  }

  async queryRejectedBurns(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<RejectedBurn>[]> {
    const res = await this.query(offset, RejectedBurn);
    return res.filter((r) => r.payload.burn.operator === operator);
  }

  async queryExecutedBurns(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<ExecutedBurn>[]> {
    const res = await this.query(offset, ExecutedBurn);
    return res.filter((r) => r.payload.burn.operator === operator);
  }

  async queryFailedBurns(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<FailedBurn>[]> {
    const res = await this.query(offset, FailedBurn);
    return res.filter((r) => r.payload.burn.operator === operator);
  }

  async queryTransferRequests(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<TransferRequest>[]> {
    const res = await this.query(offset, TransferRequest);
    return res.filter((r) => r.payload.transfer.operator === operator);
  }

  async queryTransferOffers(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<TransferOffer>[]> {
    const res = await this.query(offset, TransferOffer);
    return res.filter((r) => r.payload.transfer.operator === operator);
  }

  async queryAcceptedTransfers(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<AcceptedTransfer>[]> {
    const res = await this.query(offset, AcceptedTransfer);
    return res.filter((r) => r.payload.transfer.operator === operator);
  }

  async queryRejectedTransfers(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<RejectedTransfer>[]> {
    const res = await this.query(offset, RejectedTransfer);
    return res.filter((r) => r.payload.transfer.operator === operator);
  }

  async queryExecutedTransfers(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<ExecutedTransfer>[]> {
    const res = await this.query(offset, ExecutedTransfer);
    return res.filter((r) => r.payload.transfer.operator === operator);
  }

  async queryFailedTransfers(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<FailedTransfer>[]> {
    const res = await this.query(offset, FailedTransfer);
    return res.filter((r) => r.payload.transfer.operator === operator);
  }

  async queryLockRequests(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<LockRequest>[]> {
    const res = await this.query(offset, LockRequest);
    return res.filter((r) => r.payload.lock.operator === operator);
  }

  async queryLockOffers(offset: number, operator: string): Promise<TemplateContract<LockOffer>[]> {
    const res = await this.query(offset, LockOffer);
    return res.filter((r) => r.payload.lock.operator === operator);
  }

  async queryAcceptedLocks(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<AcceptedLock>[]> {
    const res = await this.query(offset, AcceptedLock);
    return res.filter((r) => r.payload.lock.operator === operator);
  }

  async queryRejectedLocks(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<RejectedLock>[]> {
    const res = await this.query(offset, RejectedLock);
    return res.filter((r) => r.payload.lock.operator === operator);
  }

  async queryExecutedLocks(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<ExecutedLock>[]> {
    const res = await this.query(offset, ExecutedLock);
    return res.filter((r) => r.payload.lock.operator === operator);
  }

  async queryFailedLocks(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<FailedLock>[]> {
    const res = await this.query(offset, FailedLock);
    return res.filter((r) => r.payload.lock.operator === operator);
  }

  async queryUnlockRequests(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<UnlockRequest>[]> {
    const res = await this.query(offset, UnlockRequest);
    return res.filter((r) => r.payload.unlock.operator === operator);
  }

  async queryUnlockOffers(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<UnlockOffer>[]> {
    const res = await this.query(offset, UnlockOffer);
    return res.filter((r) => r.payload.unlock.operator === operator);
  }

  async queryAcceptedUnlocks(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<AcceptedUnlock>[]> {
    const res = await this.query(offset, AcceptedUnlock);
    return res.filter((r) => r.payload.unlock.operator === operator);
  }

  async queryRejectedUnlocks(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<RejectedUnlock>[]> {
    const res = await this.query(offset, RejectedUnlock);
    return res.filter((r) => r.payload.unlock.operator === operator);
  }

  async queryExecutedUnlocks(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<ExecutedUnlock>[]> {
    const res = await this.query(offset, ExecutedUnlock);
    return res.filter((r) => r.payload.unlock.operator === operator);
  }

  async queryFailedUnlocks(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<FailedUnlock>[]> {
    const res = await this.query(offset, FailedUnlock);
    return res.filter((r) => r.payload.unlock.operator === operator);
  }

  async queryCoins(offset: number, party: string): Promise<TemplateContract<Amulet>[]> {
    const res = await this.query(offset, Amulet);
    return res.filter((r) => r.payload.owner === party);
  }

  async queryLockedCoins(offset: number, party: string): Promise<TemplateContract<LockedAmulet>[]> {
    const res = await this.query(offset, LockedAmulet);
    return res.filter((r) => r.payload.amulet.owner === party);
  }

  // Collateral

  async queryCollateralOperatorConfiguration(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<CollateralOperatorConfiguration> | null> {
    const configs = await this.query(offset, CollateralOperatorConfiguration);
    return configs.find((r) => r.payload.operator === operator) || null;
  }

  async queryCollateralState(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<CollateralState>[] | null> {
    const collateralStates = await this.query(offset, CollateralState);
    return collateralStates.filter((s) => s.payload.operator === operator);
  }

  async queryCollateralOperatorConfigurations(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<CollateralOperatorConfiguration>[] | null> {
    const configs = await this.query(offset, CollateralOperatorConfiguration);
    return configs.filter((r) => r.payload.operator === operator);
  }

  async queryCollateralUserServiceRequests(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<CollateralUserServiceRequest>[]> {
    const reqs = await this.query(offset, CollateralUserServiceRequest);
    return reqs.filter((r) => r.payload.operator === operator);
  }

  async queryCollateralUserService(
    offset: number,
    operator: string,
    party: string,
  ): Promise<TemplateContract<CollateralUserService> | null> {
    const svcs = await this.query(offset, CollateralUserService);
    return svcs.find((r) => r.payload.operator === operator && r.payload.user === party) || null;
  }

  async queryCollateralUserServices(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<CollateralUserService>[]> {
    const reqs = await this.query(offset, CollateralUserService);
    return reqs.filter((r) => r.payload.operator === operator);
  }

  async queryCollateralAgreementRequests(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<CollateralAgreementRequest>[]> {
    const res = await this.query(offset, CollateralAgreementRequest);
    return res.filter((r) => r.payload.operator === operator);
  }

  async queryCollateralAgreements(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<CollateralAgreement>[]> {
    const res = await this.query(offset, CollateralAgreement);
    return res.filter((r) => r.payload.operator === operator);
  }

  async queryInstructedCollateral(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<InstructedCollateral>[]> {
    const res = await this.query(offset, InstructedCollateral);
    return res.filter((r) => r.payload.operator === operator);
  }

  async queryExecutedCollateralTransfer(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<ExecutedCollateralTransfer>[]> {
    const res = await this.query(offset, ExecutedCollateralTransfer);
    return res.filter((r) => r.payload.operator === operator);
  }

  // Commercial Agreements
  async queryCommercialAgreements(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<CommercialAgreement>[]> {
    const res = await this.query(offset, CommercialAgreement);
    return res.filter((c) => c.payload.operator === operator);
  }

  async queryCommercialAgreementOffers(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<CommercialAgreementOffer>[]> {
    return (await this.query(offset, CommercialAgreementOffer)).filter(
      (offer) => offer.payload.operator === operator,
    );
  }

  async queryTokenStandardHoldings(
    offset: number,
  ): Promise<InterfaceContract<TokenStandardHolding>[]> {
    return this.queryInterface(offset, TokenStandardHolding);
  }

  async queryAllocationFactory(
    offset: number,
    operator: string,
    registrar: string,
  ): Promise<TemplateContract<AllocationFactory>[]> {
    const res = await this.query(offset, AllocationFactory);
    return res.filter((c) => c.payload.operator === operator && c.payload.registrar === registrar);
  }

  async queryAllocations(offset: number): Promise<InterfaceContract<Allocation>[]> {
    return this.queryInterface(offset, Allocation);
  }

  async queryAllocationRequestInterface(
    offset: number,
  ): Promise<InterfaceContract<AllocationRequest>[]> {
    return this.queryInterface(offset, AllocationRequest);
  }

  async queryTransferRules(
    offset: number,
    operator: string,
    registrar: string,
  ): Promise<TemplateContract<TransferRule>[]> {
    const res = await this.query(offset, TransferRule);
    return res.filter((c) => c.payload.operator === operator && c.payload.registrar === registrar);
  }

  async getLedgerEnd(): Promise<number> {
    return this.apiInstance.getV2StateLedgerEnd().then((r) => r.offset);
  }

  async create<T extends object, K, I extends string>(
    template: Template<T, K, I>,
    argument: T,
    meta?: CommandMeta,
    actAs: string[] = this.defaultSubmitter ? [this.defaultSubmitter] : [],
    readAs: string[] = [],
  ): Promise<CreateEvent<T, K, I>> {
    const commandId = hash({
      templateId: template.templateId,
      argument,
      actAs,
      readAs,
    });

    const command = {
      CreateCommand: {
        templateId: template.templateId,
        createArguments: template.encode(argument),
      },
    };

    const commands = {
      commands: {
        commands: [command],
        workflowId: meta?.workflowId || '',
        userId: this.userId,
        commandId,
        deduplicationPeriod: this.getDeduplicationDuration(),
        actAs,
        readAs,
        submissionId: uuidv4(),
        disclosedContracts: LedgerApiClientV2.toDisclosedContracts(meta),
        domainId: meta?.domainId || '',
        packageIdSelectionPreference: [],
      },
      transactionFormat: LedgerApiClientV2.getTransactionFormat(
        actAs,
        'TRANSACTION_SHAPE_ACS_DELTA',
      ),
    };

    const response = await this.apiInstance.postV2CommandsSubmitAndWaitForTransaction(commands);

    const event = response.transaction.events?.at(0);
    if (event && 'CreatedEvent' in event) {
      const createdEvent = event.CreatedEvent;
      return {
        templateId: template.templateId,
        contractId: createdEvent.contractId as ContractId<T>,
        signatories: createdEvent.signatories || [],
        observers: createdEvent.observers || [],
        key: createdEvent.contractKey as K,
        payload: template.decoder.runWithException(createdEvent.createArgument),
      };
    }
    return Promise.reject(new Error('CreatedEvent is expected from the response'));
  }

  async exercise<T extends object, C, R, K>(
    choice: Choice<T, C, R, K>,
    cid: ContractId<T>,
    argument: C,
    meta?: CommandMeta,
    actAs: string[] = this.defaultSubmitter ? [this.defaultSubmitter] : [],
    readAs: string[] = [],
  ): Promise<R> {
    const commandId = hash({
      contractId: cid,
      templateId: choice.template().templateId,
      choiceName: choice.choiceName,
      argument,
      actAs,
      readAs,
    });

    const command = {
      ExerciseCommand: {
        templateId: choice.template().templateId,
        contractId: cid,
        choice: choice.choiceName,
        choiceArgument: choice.argumentEncode(argument),
      },
    };

    const commands = {
      commands: {
        commands: [command],
        workflowId: meta?.workflowId || '',
        userId: this.userId,
        commandId,
        deduplicationPeriod: this.getDeduplicationDuration(),
        actAs,
        readAs,
        submissionId: uuidv4(),
        disclosedContracts: LedgerApiClientV2.toDisclosedContracts(meta),
        domainId: meta?.domainId || '',
        packageIdSelectionPreference: [],
      },
      transactionFormat: LedgerApiClientV2.getTransactionFormat(
        actAs,
        'TRANSACTION_SHAPE_LEDGER_EFFECTS',
      ),
    };

    const response = await this.apiInstance.postV2CommandsSubmitAndWaitForTransaction(commands);

    const event = response.transaction.events?.at(0);
    if (event && 'ExercisedEvent' in event) {
      const exercisedEvent = event.ExercisedEvent;
      if ('exerciseResult' in exercisedEvent) {
        return choice.resultDecoder.runWithException(exercisedEvent.exerciseResult);
      }
    }

    return Promise.reject(new Error('ExercisedEvent is expected from the response'));
  }

  private async query<T extends object, K, I extends string>(
    offset: number,
    template: Template<T, K, I>,
    party: string = this.defaultSubmitter,
  ): Promise<TemplateContract<T>[]> {
    return this.activeContractsForTemplate(offset, party, template.templateId).then((responses) => {
      return responses
        .map((resp) => resp.contractEntry)
        .filter(LedgerApiClientV2.isJsActiveContract)
        .map((activeContract) =>
          TemplateContract.fromJsonAPIV2(template, activeContract.JsActiveContract.createdEvent),
        );
    });
  }

  private async queryInterface<T extends object, K, I extends string>(
    offset: number,
    interfaceCompanion: InterfaceCompanion<T, K, I>,
    party: string | undefined = this.defaultSubmitter,
  ): Promise<InterfaceContract<T>[]> {
    return this.activeContractsForInterface(offset, party, interfaceCompanion.templateId).then(
      (responses) => {
        return responses
          .map((resp) => resp.contractEntry)
          .filter(LedgerApiClientV2.isJsActiveContract)
          .map((activeContract) =>
            InterfaceContract.fromJsonAPIV2(
              interfaceCompanion,
              activeContract.JsActiveContract.createdEvent,
            ),
          );
      },
    );
  }

  private static isJsActiveContract = (
    contract: JsContractEntry,
  ): contract is JsContractEntryOneOf => {
    return 'JsActiveContract' in contract;
  };

  async prepareCreate<T extends object, K, I extends string>(
    template: Template<T, K, I>,
    argument: T,
    meta?: CommandMeta,
    actAs: string[] = this.defaultSubmitter ? [this.defaultSubmitter] : [],
    readAs: string[] = [],
  ) {
    const commandId = hash({
      templateId: template.templateId,
      argument,
      actAs,
      readAs,
    });

    const command = {
      CreateCommand: {
        templateId: template.templateId,
        createArguments: template.encode(argument),
      },
    };

    const domains = await this.apiInstance.getV2StateConnectedSynchronizers(actAs[0]);
    const domain = domains.connectedSynchronizers ? domains.connectedSynchronizers[0] : undefined;

    const request = {
      userId: this.userId,
      commandId,
      commands: [command],
      actAs,
      readAs,
      disclosedContracts: LedgerApiClientV2.toDisclosedContracts(meta),
      domainId: domain?.synchronizerId || '',
      synchronizerId: domain?.synchronizerId || '',
      packageIdSelectionPreference: [],
      verboseHashing: true,
    };

    return this.apiInstance.postV2InteractiveSubmissionPrepare(request);
  }

  async prepareExercise<T extends object, C, R, K>(
    choice: Choice<T, C, R, K>,
    cid: ContractId<T>,
    argument: C,
    meta?: CommandMeta,
    actAs: string[] = this.defaultSubmitter ? [this.defaultSubmitter] : [],
    readAs: string[] = [],
  ) {
    const commandId = hash({
      contractId: cid,
      templateId: choice.template().templateId,
      choiceName: choice.choiceName,
      argument,
      actAs,
      readAs,
    });

    const command = {
      ExerciseCommand: {
        templateId: choice.template().templateId,
        contractId: cid,
        choice: choice.choiceName,
        choiceArgument: choice.argumentEncode(argument),
      },
    };

    const domains = await this.apiInstance.getV2StateConnectedSynchronizers(actAs[0]);
    const domain = domains.connectedSynchronizers ? domains.connectedSynchronizers[0] : undefined;

    const request = {
      userId: this.userId,
      commandId,
      commands: [command],
      actAs,
      readAs,
      disclosedContracts: LedgerApiClientV2.toDisclosedContracts(meta, domain?.synchronizerId),
      domainId: domain?.synchronizerId || '',
      synchronizerId: domain?.synchronizerId || '',
      packageIdSelectionPreference: [],
      verboseHashing: true,
    };

    return this.apiInstance.postV2InteractiveSubmissionPrepare(request);
  }

  async activeContractsForTemplate(offset: number, partyFilter: string, templateId: string) {
    return this.#acsHelper.activeContractsForTemplate(offset, partyFilter, templateId);
  }

  async activeContractsForInterface(
    offset: number,
    partyFilter: string | undefined,
    interfaceId: string,
  ) {
    return this.#acsHelper.activeContractsForInterface(offset, partyFilter, interfaceId);
  }

  async activeContracts(filters: Filters, offset: number, partyFilter: string | undefined) {
    const eventFormat: EventFormat = partyFilter
      ? {
          filtersByParty: {
            [partyFilter]: filters,
          },
          verbose: false,
        }
      : {
          filtersByParty: {},
          filtersForAnyParty: filters,
          verbose: false,
        };

    return this.apiInstance.postV2StateActiveContracts({
      activeAtOffset: offset,
      verbose: false,
      eventFormat,
    });
  }

  private static toDisclosedContracts(meta?: CommandMeta, domainId?: string): DisclosedContract[] {
    return (
      meta?.disclosedContracts?.map((disclosedContract) => ({
        ...disclosedContract,
        domainId: domainId || meta?.domainId || '',
        synchronizerId: domainId || meta?.domainId || '',
      })) || []
    );
  }

  private getDeduplicationDuration() {
    return {
      DeduplicationDuration: {
        value: {
          seconds: this.deduplicationDurationInSeconds,
          nanos: 0,
        },
      },
    };
  }

  private static getTransactionFormat(parties: string[], transactionShape: string) {
    const filtersByParties = parties.reduce(
      (acc, party) => ({
        ...acc,
        [party]: {
          cumulative: [
            {
              identifierFilter: {
                WildcardFilter: {
                  value: {
                    includeCreatedEventBlob: true,
                  },
                },
              },
            },
          ],
        },
      }),
      {},
    );

    return {
      eventFormat: {
        filtersByParty: filtersByParties,
        verbose: false,
      },
      transactionShape,
    };
  }
}
